import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Card,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  COMPLIANCE_APPROVAL_STATUSES,
  affordableComplianceApprovalStatusesMessages,
} from '../../constants/affordableComplianceApprovalStatuses';
import { RECERT_CERT_TYPE } from '../../constants/affordableCertificationTypes';
import PlanningRangeFilter from '../../components/PlanningRangeFilter';
import type { PlanningRangeOption } from '../../components/PlanningRangeFilter';
import messages from './messages';
import { RECERT_DUE_FILTER_VALUES } from './constantsV2';

const ORDER = {
  complianceApprovalStatus: [
    'approved',
    'approvedPendingSign',
    'correctionNeeded',
    'correctionNeededFinal',
    'denied',
    'onNoticeHold',
    'notStarted',
    'pending',
    'pendingFinalApproval',
  ],
};

const CertificationsSummary = ({
  propertyAffordablePrograms,
  summary,
  selectedCertificationType,
  planningRangeOptions,
  selectedPlanningRange,
  onPlanningRangeChange,
  isLoading,
  completeCertificationVisibility,
}: {
  propertyAffordablePrograms: Array<string>,
  summary: {
    recerts: Record<string, Object>,
    certifications: Record<string, Object>,
  },
  selectedCertificationType: string,
  planningRangeOptions: PlanningRangeOption[],
  selectedPlanningRange: Array<string>,
  onPlanningRangeChange: (value: Array<string>) => void,
  isLoading: boolean,
  completeCertificationVisibility: boolean,
}) => {
  const { recerts = {}, certifications = {} } = summary;
  const isRecertsTab = selectedCertificationType === RECERT_CERT_TYPE;

  const orderedCertifications = useMemo(() => {
    const orderedCertifications = [];
    // map the certifications to name as key
    const parsedCertifications = Object.entries(certifications).reduce(
      (acc, [_, { name, programs }]) => {
        acc[name] = { name, programs };
        return acc;
      },
      {},
    );
    // order the certifications
    ORDER.complianceApprovalStatus.forEach((complianceApprovalStatusKey) => {
      const statusString =
        COMPLIANCE_APPROVAL_STATUSES[complianceApprovalStatusKey];

      if (parsedCertifications[statusString]) {
        parsedCertifications[statusString].complianceApprovalStatusKey =
          complianceApprovalStatusKey;
        orderedCertifications.push(parsedCertifications[statusString]);
      }
    });

    return orderedCertifications;
  }, [certifications]);

  const shouldRender = useMemo(() => {
    if (Object.keys(recerts).length || Object.keys(certifications).length)
      return true;
  }, [recerts, certifications]);

  return shouldRender ? (
    <Stack id={'certificationsSummary'}>
      {isRecertsTab ? (
        completeCertificationVisibility ? (
          // ENG-30054: the Recerts tab's planning-range cards are the
          // shared, multi-select/OR `PlanningRangeFilter` component (Phase 1
          // output) instead of the static, non-interactive cards this block
          // used to render directly from `summary.recerts`. Bucket/date
          // filtering logic is NOT reimplemented here - `planningRangeOptions`
          // (live per-bucket counts) and `onPlanningRangeChange` come from
          // `usePlanningRangeFilter`/`handleFilterChange('recertDue', ...)` in
          // hooksV2.js, reusing the existing `recertDue` filter state as-is.
          // This entire interactive control is gated behind
          // `completeCertificationVisibility` - when off, the pre-Release-3
          // static cards render instead (see the branch below), unchanged.
          <PlanningRangeFilter
            options={planningRangeOptions}
            value={selectedPlanningRange}
            onChange={onPlanningRangeChange}
            disabled={isLoading}
          />
        ) : (
          // Pre-Release-3 static, non-interactive Recerts summary cards -
          // restored verbatim (including the >120-day bucket's own
          // completeCertificationVisibility skip, which is always true in
          // this branch by construction, but kept for fidelity with the
          // original code) for when the flag is off.
          <Stack
            direction={'row'}
            spacing={1}
            sx={{ padding: 2, overflowX: 'auto', width: '100%' }}
          >
            {Object.entries(recerts).map(([key, programs]) => {
              if (!key.length) return null;
              if (
                key === RECERT_DUE_FILTER_VALUES.UPCOMING &&
                !completeCertificationVisibility
              )
                return null;
              return (
                <Card
                  key={key}
                  sx={{
                    minWidth: 210,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant={'labelLarge'} padding={2}>
                    <FormattedMessage
                      {...(key === RECERT_DUE_FILTER_VALUES.UPCOMING
                        ? messages.upcomingSummaryCard
                        : messages[key])}
                    />
                  </Typography>
                  <Stack direction={'column'} spacing={1} sx={{ padding: 2 }}>
                    {propertyAffordablePrograms.map((PROGRAM) => {
                      const count = programs[PROGRAM] ?? 0;
                      return (
                        <Typography
                          key={PROGRAM}
                          color={count === 0 ? 'text.disabled' : 'text.primary'}
                        >
                          {PROGRAM}: {count}
                        </Typography>
                      );
                    })}
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        )
      ) : (
        <Stack
          direction={'row'}
          spacing={1}
          sx={{ padding: 2, overflowX: 'auto', width: '100%' }}
        >
          {orderedCertifications.map(
            ({ name, complianceApprovalStatusKey, programs }) => {
              return (
                <Card
                  key={name}
                  sx={{
                    minWidth: 210,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant={'labelLarge'} padding={2}>
                    <FormattedMessage
                      {...affordableComplianceApprovalStatusesMessages[
                        complianceApprovalStatusKey
                      ]}
                    />
                  </Typography>
                  <Stack direction={'column'} spacing={1} sx={{ padding: 2 }}>
                    {propertyAffordablePrograms.map((PROGRAM) => {
                      const count = programs[PROGRAM] ?? 0;
                      return (
                        <Typography
                          key={PROGRAM}
                          color={count === 0 ? 'text.disabled' : 'text.primary'}
                        >
                          {PROGRAM}: {count}
                        </Typography>
                      );
                    })}
                  </Stack>
                </Card>
              );
            },
          )}
        </Stack>
      )}
    </Stack>
  ) : null;
};

CertificationsSummary.defaultProps = {
  summary: {
    recerts: {},
    certifications: {},
  },
  planningRangeOptions: [],
  selectedPlanningRange: [],
  onPlanningRangeChange: () => {},
  isLoading: false,
  completeCertificationVisibility: false,
};
export default CertificationsSummary;
